'use client';

import { useEffect, useRef, useSyncExternalStore, type RefObject } from 'react';

export type TourPlacement =
	| 'top'
	| 'top-start'
	| 'top-end'
	| 'bottom'
	| 'bottom-start'
	| 'bottom-end'
	| 'left'
	| 'left-start'
	| 'left-end'
	| 'right'
	| 'right-start'
	| 'right-end';

export type TourTargetRef = string | Element | (() => Element | null);

export type TourStep = {
	target: TourTargetRef;
	title: string;
	description?: string;
	video?: string;
	placement?: TourPlacement;
	menu?: string;
	slot?: string;
};

export type TourDefinition = {
	id: string;
	steps: TourStep[];
	onFinish?: () => void;
	onDismiss?: () => void;
	onStepChange?: (index: number) => void;
};

export type TipDefinition = TourStep & {
	id: string;
	onClose?: () => void;
};

export type TourOptions = { force?: boolean };

type Ativo = {
	tour: TourDefinition;
	index: number;
	unico: boolean;
};

type NaFila = { tour: TourDefinition; unico: boolean; force: boolean };

const listeners = new Set<() => void>();
const fila: NaFila[] = [];
let ativo: Ativo | null = null;
let direcao: 1 | -1 = 1;
let algumExibido = false;

const subscribe = (l: () => void) => {
	listeners.add(l);
	return () => {
		listeners.delete(l);
	};
};
const notify = () => listeners.forEach((l) => l());
const getAtivo = () => ativo;
const getServerAtivo = (): Ativo | null => null;

export const getActiveTour = getAtivo;

const chave = (id: string) => `empreender-ui:tour:${id}`;

export function isTourDismissed(id: string) {
	try {
		return localStorage.getItem(chave(id)) === '1';
	} catch {
		return false;
	}
}

const gravarDispensa = (id: string) => {
	try {
		localStorage.setItem(chave(id), '1');
	} catch {
		return;
	}
};

export function resetTour(id: string) {
	try {
		localStorage.removeItem(chave(id));
	} catch {
		return;
	}
}

const MAXIMO_DE_VIDEOS = 4;
const videos = new Map<string, HTMLVideoElement>();

const descartarSobras = () => {
	for (const [url, video] of videos) {
		if (videos.size <= MAXIMO_DE_VIDEOS) return;
		if (video.isConnected) continue;

		video.removeAttribute('src');
		video.load();
		videos.delete(url);
	}
};

export function precarregarVideo(url?: string) {
	if (!url || typeof document === 'undefined') return null;

	const guardado = videos.get(url);

	if (guardado) {
		videos.delete(url);
		videos.set(url, guardado);
		return guardado;
	}

	const video = document.createElement('video');
	video.muted = true;
	video.defaultMuted = true;
	video.loop = true;
	video.playsInline = true;
	video.disablePictureInPicture = true;
	video.preload = 'auto';
	video.src = url;

	videos.set(url, video);
	descartarSobras();

	return video;
}

const precarregarEntorno = (tour: TourDefinition, index: number) => {
	precarregarVideo(tour.steps[index]?.video);
	precarregarVideo(tour.steps[index + 1]?.video);
};

const ativar = (tour: TourDefinition, index: number, unico: boolean) => {
	ativo = { tour, index, unico };
	precarregarEntorno(tour, index);
	notify();
};

const proximoDaFila = () => {
	while (fila.length > 0) {
		const item = fila.shift()!;

		if (!item.force && isTourDismissed(item.tour.id)) continue;
		if (item.tour.steps.length === 0) continue;

		direcao = 1;
		algumExibido = false;
		ativar(item.tour, 0, item.unico);
		return;
	}

	ativo = null;
	notify();
};

const jaNaFila = (id: string) => ativo?.tour.id === id || fila.some((item) => item.tour.id === id);

const enfileirar = (item: NaFila) => {
	if (!item.force && isTourDismissed(item.tour.id)) return;
	if (item.tour.steps.length === 0 || jaNaFila(item.tour.id)) return;

	fila.push(item);

	if (!ativo) proximoDaFila();
};

export function startTour(tour: TourDefinition, { force = false }: TourOptions = {}) {
	enfileirar({ tour, unico: tour.steps.length === 1, force });
}

export function showTip({ id, onClose, ...passo }: TipDefinition, { force = false }: TourOptions = {}) {
	enfileirar({
		tour: { id, steps: [passo], onFinish: onClose, onDismiss: onClose },
		unico: true,
		force,
	});
}

const encerrar = (gravar: boolean, aviso?: (tour: TourDefinition) => void) => {
	if (!ativo) return;

	const { tour } = ativo;

	if (gravar) gravarDispensa(tour.id);

	aviso?.(tour);
	proximoDaFila();
};

const concluir = () => encerrar(true, (tour) => tour.onFinish?.());

export function dismissTour() {
	encerrar(true, (tour) => tour.onDismiss?.());
}

export function abandonTour() {
	encerrar(false);
}

export function stopTour() {
	fila.length = 0;
	encerrar(false);
}

const irPara = (index: number) => {
	if (!ativo) return;

	if (index >= ativo.tour.steps.length) {
		if (algumExibido) concluir();
		else abandonTour();
		return;
	}

	if (index < 0) return;

	ativar(ativo.tour, index, ativo.unico);
};

export function nextStep() {
	if (!ativo) return;
	direcao = 1;
	irPara(ativo.index + 1);
}

export function previousStep() {
	if (!ativo || ativo.index === 0) return;
	direcao = -1;
	irPara(ativo.index - 1);
}

export function skipStep() {
	if (!ativo) return;

	const destino = ativo.index + direcao;

	if (destino < 0) {
		direcao = 1;
		irPara(ativo.index + 1);
		return;
	}

	irPara(destino);
}

export function markStepShown(index: number) {
	if (!ativo || ativo.index !== index) return;

	algumExibido = true;
	ativo.tour.onStepChange?.(index);
}

export function useTour() {
	const atual = useSyncExternalStore(subscribe, getAtivo, getServerAtivo);

	return {
		tour: atual?.tour ?? null,
		index: atual?.index ?? 0,
		total: atual?.tour.steps.length ?? 0,
		unico: atual?.unico ?? false,
		next: nextStep,
		back: previousStep,
		dismiss: dismissTour,
		stop: stopTour,
	};
}

type ObterAlvo = () => Element | null;

const alvos = new Map<string, ObterAlvo>();

export function registrarAlvo(id: string, obter: ObterAlvo) {
	alvos.set(id, obter);

	return () => {
		if (alvos.get(id) === obter) alvos.delete(id);
	};
}

export function resolverAlvo(target: TourTargetRef): Element | null {
	if (typeof target === 'string') return alvos.get(target)?.() ?? null;
	if (typeof target === 'function') return target();

	return target.isConnected ? target : null;
}

export function useTourTarget(id: string | undefined, ref: RefObject<Element | null>, primeiroFilho = false) {
	const primeiro = useRef(primeiroFilho);
	primeiro.current = primeiroFilho;

	useEffect(() => {
		if (!id) return;

		return registrarAlvo(id, () => {
			const el = ref.current;

			return primeiro.current ? el?.firstElementChild ?? null : el;
		});
	}, [id, ref]);
}

let explicitas = 0;
const getExplicitas = () => explicitas;
const getServerExplicitas = () => 0;

export function useTourExplicita() {
	useEffect(() => {
		explicitas += 1;
		notify();

		return () => {
			explicitas -= 1;
			notify();
		};
	}, []);
}

export function useTourExplicitas() {
	return useSyncExternalStore(subscribe, getExplicitas, getServerExplicitas);
}
